import React from "react";
import moment from "moment";

import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";

import HistoryIcon from "@material-ui/icons/History";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import InputAdornment from "@material-ui/core/InputAdornment";
import ListItemText from "@material-ui/core/ListItemText";

import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";

import { makeStyles } from "@material-ui/core/styles";

import Table from "jazasoft/lib/mui/components/Table";

import { syntaxHighlight } from "../utils/helpers";
import useQuery from "../hooks/useQuery";

const HISTORY_DRAWER_WIDTH = 1050;
const DETAIL_DRAWER_WIDTH = 650;

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: HISTORY_DRAWER_WIDTH,
    maxWidth: "92vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#ffffff"
  },

  detailDrawerPaper: {
    width: DETAIL_DRAWER_WIDTH,
    maxWidth: "92vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    boxShadow: theme.shadows[16]
  },

  header: {
    minHeight: 74,
    padding: theme.spacing(2, 2.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    minWidth: 0
  },

  titleIcon: {
    marginRight: theme.spacing(1.25),
    color: theme.palette.text.secondary
  },

  titleText: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.3
  },

  subtitle: {
    display: "block",
    color: theme.palette.text.secondary,
    marginTop: 2
  },
  summaryBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    minHeight: 64,
    padding: theme.spacing(1, 2),
    backgroundColor: "#fafafa",
    flexShrink: 0
  },

  filterSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },

  eventFilter: {
    width: 250,

    "& .MuiOutlinedInput-root": {
      height: 40,
      borderRadius: 6,
      backgroundColor: "#ffffff"
    },

    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      paddingTop: 10,
      paddingBottom: 10
    }
  },

  filterIcon: {
    color: theme.palette.text.secondary,
    fontSize: 19
  },

  clearFilterButton: {
    marginLeft: -4
  },

  menuItemContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
  },

  menuItemCount: {
    minWidth: 28,
    height: 21,
    marginLeft: theme.spacing(2),
    fontSize: 11,
    backgroundColor: "#f1f3f4"
  },

  activeFilterText: {
    fontWeight: 500
  },

  filterMenuItem: {
    fontSize: 13
  },
  countChip: {
    height: 26,
    fontSize: 12
  },

  content: {
    flex: 1,
    minHeight: 0,
    overflow: "auto"
  },

  tableWrapper: {
    minWidth: 900,

    "& tbody tr:nth-child(even)": {
      backgroundColor: "#fcfcfd"
    },

    "& tbody tr:hover": {
      backgroundColor: "#f7f9fb"
    }
  },

  footer: {
    minHeight: 56,
    padding: theme.spacing(1, 2),
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexShrink: 0
  },

  timeValue: {
    lineHeight: 1.45,
    color: "#424242",
    whiteSpace: "nowrap"
  },

  userValue: {
    lineHeight: 1.45,
    color: "#303030",
    fontWeight: 500
  },

  eventBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "3px 9px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap"
  },

  eventCreated: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32"
  },

  eventUpdated: {
    backgroundColor: "#fff8e1",
    color: "#ef6c00"
  },

  eventDeleted: {
    backgroundColor: "#ffebee",
    color: "#c62828"
  },

  eventGenerated: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0"
  },

  dataCell: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    gap: theme.spacing(1)
  },

  dataPreview: {
    flex: 1,
    minWidth: 0,
    maxWidth: 430,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#5f6368",
    fontFamily: '"Roboto Mono", Consolas, monospace',
    fontSize: 12
  },

  viewButton: {
    minWidth: 48,
    height: 24,
    padding: "0 10px",
    borderRadius: 12,
    border: "1px solid #9eacb8",
    color: "#52697a",
    backgroundColor: "#ffffff",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "none",
    flexShrink: 0,

    "&:hover": {
      backgroundColor: "#f5f8fa",
      borderColor: "#607d8b"
    }
  },

  detailHeader: {
    minHeight: 74,
    padding: theme.spacing(2, 2.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0
  },

  detailHeaderLeft: {
    display: "flex",
    alignItems: "center",
    minWidth: 0
  },

  backButton: {
    marginRight: theme.spacing(1)
  },

  detailTitleWrapper: {
    minWidth: 0
  },

  detailTitle: {
    fontSize: 18,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  detailContent: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: theme.spacing(2.5),
    backgroundColor: "#fafafa"
  },

  detailBox: {
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    backgroundColor: "#ffffff",
    fontFamily: '"Roboto Mono", Consolas, monospace',
    fontSize: 13,
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },

  emptyDetail: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(4)
  }
}));

const historyColumn = [
  {
    dataKey: "time",
    title: "Time",
    options: { width: 125 }
  },
  {
    dataKey: "user",
    title: "User",
    options: { width: 170 }
  },
  {
    dataKey: "event",
    title: "Event",
    options: { width: 240 }
  },
  {
    dataKey: "data",
    title: "Data"
  }
];

const formatEventLabel = event =>
  String(event || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());

const getFormattedData = log => {
  if (!log) {
    return null;
  }

  if (log.diff) {
    return log.diff;
  }

  const rawData = log.data || log.apiData;

  if (!rawData) {
    return null;
  }

  try {
    const parsedData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;

    return syntaxHighlight(JSON.stringify(parsedData, null, 4));
  } catch (error) {
    return String(rawData);
  }
};

const HistoryDrawer = ({ url, open, onClose }) => {
  const classes = useStyles();
  const [selectedEvent, setSelectedEvent] = React.useState("ALL");

  React.useEffect(() => {
    if (!open) {
      setDetailState({
        open: false,
        dataStr: null,
        event: null
      });

      setSelectedEvent("ALL");
    }
  }, [open]);

  const [detailState, setDetailState] = React.useState({
    open: false,
    dataStr: null,
    event: null
  });

  const { data, loading } = useQuery(open && url ? { url } : {});

  React.useEffect(() => {
    if (!open) {
      setDetailState({
        open: false,
        dataStr: null,
        event: null
      });
    }
  }, [open]);

  const closeHistoryDrawer = () => {
    setDetailState({
      open: false,
      dataStr: null,
      event: null
    });

    onClose();
  };

  const closeDetailDrawer = () => {
    setDetailState({
      open: false,
      dataStr: null,
      event: null
    });
  };

  const onViewClick = log => event => {
    event.preventDefault();
    event.stopPropagation();

    setDetailState({
      open: true,
      dataStr: getFormattedData(log),
      event: log?.event || "History Detail"
    });
  };

  const getEventClassName = event => {
    const value = String(event || "").toUpperCase();

    if (value.includes("CREATED")) {
      return classes.eventCreated;
    }

    if (value.includes("DELETED")) {
      return classes.eventDeleted;
    }

    if (value.includes("GENERATED")) {
      return classes.eventGenerated;
    }

    return classes.eventUpdated;
  };

  const sortedData = React.useMemo(() => {
    if (!Array.isArray(data) || loading) {
      return [];
    }

    return data
      .map(item => ({
        ...item,
        timestamp: item.timestamp || item.createdAt,
        user: item.user || item.createdBy
      }))
      .sort((first, second) => Number(second.timestamp || 0) - Number(first.timestamp || 0));
  }, [data, loading]);

  const eventOptions = React.useMemo(() => {
    const eventCountMap = sortedData.reduce((result, item) => {
      if (!item.event) {
        return result;
      }

      result[item.event] = (result[item.event] || 0) + 1;

      return result;
    }, {});

    return Object.keys(eventCountMap)
      .sort((first, second) => formatEventLabel(first).localeCompare(formatEventLabel(second)))
      .map(event => ({
        value: event,
        label: formatEventLabel(event),
        count: eventCountMap[event]
      }));
  }, [sortedData]);

  const rows = sortedData
    .filter(item => selectedEvent === "ALL" || String(item.event) === String(selectedEvent))
    .map(item => {
      const rawValue = item.diff
        ? item.diff.trim() === "Diff:" || item.diff.trim() === ""
          ? "No field-level changes recorded"
          : item.diff
        : item.data || item.apiData;

      return {
        ...item,

        time: (
          <span className={classes.timeValue}>
            {item.timestamp ? moment(item.timestamp).format("DD MMM, YY") : "-"}
            <br />
            {item.timestamp ? moment(item.timestamp).format("hh:mm a") : ""}
          </span>
        ),

        user: <span className={classes.userValue}>{item.user || "-"}</span>,

        event: <span className={`${classes.eventBadge} ${getEventClassName(item.event)}`}>{formatEventLabel(item.event)}</span>,

        data: rawValue ? (
          <div className={classes.dataCell}>
            <Tooltip title={String(rawValue)} placement="top-start" enterDelay={500}>
              <span className={classes.dataPreview}>{String(rawValue).replace(/\s+/g, " ")}</span>
            </Tooltip>

            <Button variant="outlined" className={classes.viewButton} onClick={onViewClick(item)}>
              View
            </Button>
          </div>
        ) : (
          "-"
        )
      };
    });

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={closeHistoryDrawer}
        classes={{
          paper: classes.drawerPaper
        }}>
        <Box className={classes.header}>
          <div className={classes.headerLeft}>
            <HistoryIcon className={classes.titleIcon} />

            <div>
              <Typography className={classes.titleText}>History</Typography>

              <Typography variant="caption" className={classes.subtitle}>
                Review user activity and recorded changes
              </Typography>
            </div>
          </div>

          <IconButton size="small" onClick={closeHistoryDrawer} aria-label="Close history">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box className={classes.summaryBar}>
          <div className={classes.filterSection}>
            <FormControl variant="outlined" size="small" className={classes.eventFilter}>
              <Select
                id="history-event-filter"
                value={selectedEvent}
                onChange={event => setSelectedEvent(event.target.value)}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon className={classes.filterIcon} />
                  </InputAdornment>
                }
                renderValue={value =>
                  value === "ALL" ? <span>All Events</span> : <span className={classes.activeFilterText}>{formatEventLabel(value)}</span>
                }>
                <MenuItem value="ALL">
                  <div className={classes.menuItemContent}>
                    <ListItemText primary="All Events" />

                    <Chip size="small" label={sortedData.length} className={classes.menuItemCount} />
                  </div>
                </MenuItem>

                <Divider />

                {eventOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <div className={classes.menuItemContent}>
                      <ListItemText primary={option.label} />

                      <Chip size="small" label={option.count} className={classes.menuItemCount} />
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedEvent !== "ALL" && (
              <Tooltip title="Clear event filter">
                <IconButton
                  size="small"
                  className={classes.clearFilterButton}
                  onClick={() => setSelectedEvent("ALL")}
                  aria-label="Clear event filter">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <Chip
            variant="outlined"
            size="small"
            className={classes.countChip}
            label={
              loading
                ? "Loading..."
                : selectedEvent === "ALL"
                  ? `${rows.length} ${rows.length === 1 ? "event" : "events"}`
                  : `${rows.length} of ${sortedData.length} events`
            }
          />
        </Box>

        <Divider />

        <Box className={classes.content}>
          <div className={classes.tableWrapper}>
            <Table columns={historyColumn} rows={rows} loading={loading} emptyMessage="No History Available" />
          </div>
        </Box>

        <Divider />

        <Box className={classes.footer}>
          <Button onClick={closeHistoryDrawer} color="primary">
            Close
          </Button>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={detailState.open}
        onClose={closeDetailDrawer}
        classes={{
          paper: classes.detailDrawerPaper
        }}>
        <Box className={classes.detailHeader}>
          <div className={classes.detailHeaderLeft}>
            <IconButton size="small" className={classes.backButton} onClick={closeDetailDrawer} aria-label="Back to history">
              <ArrowBackIcon />
            </IconButton>

            <div className={classes.detailTitleWrapper}>
              <Typography className={classes.detailTitle}>{formatEventLabel(detailState.event || "History Detail")}</Typography>

              <Typography variant="caption" color="textSecondary">
                Complete recorded information
              </Typography>
            </div>
          </div>

          <IconButton size="small" onClick={closeDetailDrawer} aria-label="Close history detail">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box className={classes.detailContent}>
          {detailState.dataStr ? (
            <div
              className={classes.detailBox}
              dangerouslySetInnerHTML={{
                __html: detailState.dataStr
              }}
            />
          ) : (
            <div className={classes.emptyDetail}>No detail available</div>
          )}
        </Box>

        <Divider />

        <Box className={classes.footer}>
          <Button onClick={closeDetailDrawer} color="primary">
            Back
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default HistoryDrawer;
